import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import React, { useState } from 'react';
import { useAppSelector } from '../../app/hooks';
import OrderReview from '../../features/order-review/OrderReview';
import CopyAllToDraftControl from '../OrderReview/CopyAllToDraftControl';
import { selectCanCopyToDraft } from '../../redux/features/agentMode/agentMode.selector';
import { selectIsShadowModeEnabled } from '../../redux/features/config/config.selector';
import { selectCartItemsQuantity } from '../../selectors/cart';
import { selectIsCarPresent } from '../../selectors/message';
import Colors from '../../utils/color';
import { HITL3_PADDING } from '../../utils/hitl3Spacing';
import ConsoleCard from './ConsoleCard';

/**
 * HITL-1647 — one name for the right column, in both modes.
 *
 * It says the thing that matters: this is what the customer gets and what the
 * restaurant makes. It is also true in `Shadow` and in `Intercept`, which is
 * the property the two old names were each only half of.
 *
 * "Main cart" was considered and rejected. It and "My Cart" both begin with M,
 * are the same length, and sit ~180px apart in the same position — poor contrast
 * for someone scanning at speed with a customer talking at them. "Order"
 * contrasts on the first letter and on meaning:
 *
 *     LIVE ORDER        |        MY CART
 *
 * HITL-1649 made that contrast carry more weight than it did here: My Cart's
 * standing `· not sent` caption is gone, so this title is now the ONLY thing on
 * screen saying which of the two columns the customer is getting.
 *
 * Title case here because `ConsoleCard` uppercases every card title in CSS; the
 * neighbouring `MY_CART_TITLE` is written the same way.
 */
export const LIVE_ORDER_TITLE = 'Live Order';

/** What this column is called with `hitl_shadow_mode` off. Unchanged, forever. */
export const LEGACY_CART_TITLE = 'Agent Cart';

const useStyles = makeStyles<Theme>(() => ({
  count: {
    color: Colors.silverChalice,
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  // Scrolls the cart line items internally so the scrollbar stays inside the
  // card, not on the outer resizable panel. 8px left/right inset around
  // OrderReview; no top/bottom inset.
  itemsScroll: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: `0 ${HITL3_PADDING}`,
  },
  // HITL-1649: the cart-level copy, pinned under the body. Rule for rule
  // `MyCartCard.promoteFooter`, because the two columns' one cart-level action
  // each should sit at the same height and read as a pair.
  copyFooter: {
    flexShrink: 0,
    padding: HITL3_PADDING,
    borderTop: `1px solid ${Colors.alto}`,
  },
}));

interface AgentCartCardProps {
  // Controlled collapse so the parent drives the resizable panel; falls back to
  // internal state (default open) when used standalone.
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * AGENT CART card (right pane, top). The working cart's line items + modifiers
 * (OrderReview) under an "Agent Cart" heading with a live item count. The totals
 * and Cancel / Finish actions are NOT here — they are pinned in the RightSidebar
 * footer so they stay visible however the cards are resized or collapsed.
 */
const AgentCartCard = ({
  open: openProp,
  onOpenChange,
}: AgentCartCardProps = {}) => {
  const classes = useStyles();
  const [openState, setOpenState] = useState(true);
  const open = openProp ?? openState;
  const toggleOpen = () => {
    const next = !open;
    if (openProp === undefined) setOpenState(next);
    onOpenChange?.(next);
  };

  const itemQuantities = useAppSelector(selectCartItemsQuantity);
  const isCarPresent = useAppSelector(selectIsCarPresent);
  // HITL-1647: one name in both modes. This column used to be `AI Cart` while
  // the AI drove and `Agent Cart` once the agent took over — two names for the
  // one thing that never changes about it, which is that this is the order.
  const isShadowModeEnabled = useAppSelector(selectIsShadowModeEnabled);
  // HITL-1649: `Shadow` only, the gate this control has always had. In
  // `Intercept` these lines already ARE the agent's order, so there is nothing
  // to copy into a draft and the footer is not rendered at all.
  const canCopyToDraft = useAppSelector(selectCanCopyToDraft);

  // selectCartItemsQuantity is a per-item map; total = sum of the quantities.
  const itemCount = React.useMemo(
    () =>
      Object.values(itemQuantities ?? {}).reduce(
        (sum, qty) => sum + (qty || 0),
        0
      ),
    [itemQuantities]
  );

  // `ConsoleCard` renders a footer in both the open and collapsed states, so
  // collapsing the Live order to read something else does not take the copy
  // away with it — the same promise My Cart's takeover footer makes.
  const copyFooter = canCopyToDraft ? (
    <div className={classes.copyFooter}>
      <CopyAllToDraftControl />
    </div>
  ) : undefined;

  return (
    <ConsoleCard
      footer={copyFooter}
      icon={<ShoppingCartOutlinedIcon />}
      title={isShadowModeEnabled ? LIVE_ORDER_TITLE : LEGACY_CART_TITLE}
      open={open}
      onToggle={toggleOpen}
      testId="agent-cart-card"
      // OrderReview is heavy and its effects must keep syncing the order even
      // while collapsed — keep it mounted (hidden) so toggling is instant.
      keepBodyMounted
      headerRight={
        isCarPresent ? (
          <span className={classes.count}>
            {itemCount} {itemCount === 1 ? 'item' : 'items'}
          </span>
        ) : undefined
      }
    >
      <div id="agent-cart" className={classes.itemsScroll}>
        <OrderReview />
      </div>
    </ConsoleCard>
  );
};

export default AgentCartCard;
